import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Box } from '@mui/material';

import {
  CartSection,
  CartItemsContainer,
  CartItemCard,
  CartItemHeader,
  CartItemInfo,
  CartItemTypeLabel,
  CartItemName,
  CartItemSubLabel,
  CartItemRow,
  CartItemLabel,
  CartItemValue,
  CartItemValueHighlight,
  CartDeleteButton,
  CartItemPriceInfo,
  CartPriceColumn,
  CartPriceRow,
  CartPriceLabel,
  CartPriceValue,
  CartPriceValueOutstanding,
  CartPriceValueFinal,
  CartPriceDivider,
  CartSummaryTable,
  CartSummaryLabelColumn,
  CartSummaryValueColumn,
  CartSummaryCell,
  CartSummaryLabel,
  CartSummaryLabelOutstanding,
  CartSummaryLabelTotal,
  CartSummaryValue,
  CartSummaryValueLarge,
  CartSummaryValueOutstanding,
  CartSummaryValueTotal,
  EmptyCartContainer,
  EmptyCartText,
} from './EnterSaleInfoModalStyled';

const EnterSaleInfoModalCartSection = ({
  cartItems,
  onRemoveItem,
  formatInteger,
  isPointUsageEnabled = false,
  cartEarnPoint = 0,
}) => {
  const { t } = useTranslation();

  // 카트 총계 계산
  const cartSummary = React.useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return {
        totalCount: 0,
        totalPrice: 0,
        totalDiscount: 0,
        totalOutstanding: 0,
        totalFinalPrice: 0,
        totalUsedPoint: 0,
      };
    }

    return cartItems.reduce(
      (acc, item) => ({
        totalCount: acc.totalCount + 1,
        totalPrice: acc.totalPrice + (Number(String(item.price || 0).replace(/,/g, '')) || 0),
        totalDiscount: acc.totalDiscount + (Number(String(item.discountAmount || 0).replace(/,/g, '')) || 0),
        totalOutstanding: acc.totalOutstanding + (Number(String(item.outstandingPrice || 0).replace(/,/g, '')) || 0),
        totalFinalPrice: acc.totalFinalPrice + (Number(String(item.finalPrice || 0).replace(/,/g, '')) || 0),
        totalUsedPoint: acc.totalUsedPoint + (Number(item.usedPoint) || 0),
      }),
      {
        totalCount: 0,
        totalPrice: 0,
        totalDiscount: 0,
        totalOutstanding: 0,
        totalFinalPrice: 0,
        totalUsedPoint: 0,
      },
    );
  }, [cartItems]);

  // 총 결제금액에서 사용 포인트를 차감한, 실제 결제수단으로 청구될 금액
  const netTotalFinalPrice = cartSummary.totalFinalPrice - cartSummary.totalUsedPoint;

  // 상품 타입에 따른 라벨 반환
  const getTypeLabel = type => {
    switch (type) {
      case 'ticket':
        return t('수강권');
      case 'pass':
        return t('이용권');
      case 'locker':
        return t('락커');
      case 'product':
        return t('상품');
      default:
        return type;
    }
  };

  // 수업 타입에 따른 라벨 반환
  const getClassTypeLabel = classType => {
    switch (classType) {
      case 'PRIVATE_LESSON':
        return t('개인');
      case 'GROUP_LESSON':
        return t('그룹');
      case 'DUET_LESSON':
        return t('듀엣');
      default:
        return classType;
    }
  };

  // 날짜 포맷팅
  const formatDate = date => {
    if (!date) return '';
    return moment(date).format('YYYY.MM.DD');
  };

  return (
    <CartSection>
      {/* 담긴 상품 리스트 */}
      <CartItemsContainer>
        {cartItems.length === 0 ? (
          <EmptyCartContainer>
            <EmptyCartText>{t('담긴 상품이 없습니다.')}</EmptyCartText>
            <EmptyCartText>{t('상품을 선택 후 담기 버튼을 눌러주세요.')}</EmptyCartText>
          </EmptyCartContainer>
        ) : (
          cartItems.map((item, index) => (
            <CartItemCard key={item.id || index}>
              <CartItemHeader>
                <CartItemInfo>
                  {/* 상품 타입 및 이름 */}
                  <CartItemRow>
                    <CartItemTypeLabel>{getTypeLabel(item.type)}</CartItemTypeLabel>
                    {item.classType && <CartItemSubLabel>{getClassTypeLabel(item.classType)}</CartItemSubLabel>}
                    <CartItemName>{item.name}</CartItemName>
                  </CartItemRow>

                  {/* 락커 정보 (락커 상품인 경우) */}
                  {item.type === 'locker' && item.lockerInfo && (
                    <CartItemRow>
                      <CartItemLabel>{t('락커정보')}</CartItemLabel>
                      <CartItemValueHighlight>{item.lockerInfo}</CartItemValueHighlight>
                    </CartItemRow>
                  )}

                  {/* 이용횟수 (수강권인 경우) */}
                  {item.usageCount && (
                    <CartItemRow>
                      <CartItemLabel>{t('이용횟수')}</CartItemLabel>
                      <CartItemValueHighlight>{item.usageCount}회</CartItemValueHighlight>
                    </CartItemRow>
                  )}

                  {/* 유효기간 */}
                  {item.startDate && (
                    <CartItemRow>
                      <CartItemLabel>{t('유효기간')}</CartItemLabel>
                      <CartItemValue>
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </CartItemValue>
                    </CartItemRow>
                  )}

                  {/* 판매 담당자 — N명이면 "이름 30%" 칩으로 줄바꿈. 한 문장으로 두면 이름 중간에서 꺾인다 */}
                  {item.branchManagerName && (
                    <CartItemRow sx={{ alignItems: 'flex-start' }}>
                      <CartItemLabel sx={{ flexShrink: 0 }}>{t('판매담당자')}</CartItemLabel>
                      {item.branchManagerIds && item.branchManagerIds.length >= 2 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minWidth: 0 }}>
                          {item.branchManagerName.split(' · ').map(seller => (
                            <Box
                              key={seller}
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#4AB3BC',
                                backgroundColor: '#e6f5f5',
                                borderRadius: '999px',
                                padding: '1px 8px',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.5,
                              }}>
                              {seller}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <CartItemValue>{item.branchManagerName}</CartItemValue>
                      )}
                    </CartItemRow>
                  )}
                </CartItemInfo>

                {/* 삭제 버튼 */}
                <CartDeleteButton onClick={() => onRemoveItem(index)}>{t('삭제')}</CartDeleteButton>
              </CartItemHeader>

              {/* 가격 정보 */}
              <CartItemPriceInfo>
                <CartPriceColumn>
                  <CartPriceRow>
                    <CartPriceLabel>{t('판매금액')} :</CartPriceLabel>
                    <CartPriceValue>{formatInteger(item.price)}원</CartPriceValue>
                  </CartPriceRow>
                  <CartPriceRow>
                    <CartPriceLabel>{t('할인금액')} :</CartPriceLabel>
                    <CartPriceValue>{formatInteger(item.discountAmount)}원</CartPriceValue>
                  </CartPriceRow>
                </CartPriceColumn>

                <CartPriceDivider />

                <CartPriceColumn>
                  <CartPriceRow>
                    <CartPriceLabel>{t('미수금')} :</CartPriceLabel>
                    <CartPriceValueOutstanding>{formatInteger(item.outstandingPrice)}원</CartPriceValueOutstanding>
                  </CartPriceRow>
                  {/* 사용 포인트 (있을 때만) — 결제금액이 항상 맨 아래 */}
                  {Number(item.usedPoint) > 0 && (
                    <CartPriceRow>
                      <CartPriceLabel>{t('사용 포인트')} :</CartPriceLabel>
                      <CartPriceValueOutstanding>-{formatInteger(item.usedPoint)}P</CartPriceValueOutstanding>
                    </CartPriceRow>
                  )}
                  <CartPriceRow>
                    <CartPriceLabel>{t('결제금액')} :</CartPriceLabel>
                    <CartPriceValueFinal>
                      {formatInteger(
                        (Number(String(item.finalPrice || 0).replace(/,/g, '')) || 0) - (Number(item.usedPoint) || 0),
                      )}
                      원
                    </CartPriceValueFinal>
                  </CartPriceRow>
                </CartPriceColumn>
              </CartItemPriceInfo>
            </CartItemCard>
          ))
        )}
      </CartItemsContainer>

      {/* 요약 테이블 */}
      <CartSummaryTable>
        <CartSummaryLabelColumn>
          <CartSummaryCell isFirst isLabel>
            <CartSummaryLabel>{t('상품수량')}</CartSummaryLabel>
          </CartSummaryCell>
          <CartSummaryCell isLabel>
            <CartSummaryLabel>{t('판매금액')}</CartSummaryLabel>
          </CartSummaryCell>
          <CartSummaryCell isLabel>
            <CartSummaryLabel>{t('할인금액')}</CartSummaryLabel>
          </CartSummaryCell>
          <CartSummaryCell isLabel>
            <CartSummaryLabelOutstanding>{t('미수금')}</CartSummaryLabelOutstanding>
          </CartSummaryCell>
          {cartSummary.totalUsedPoint > 0 && (
            <CartSummaryCell isLabel>
              <CartSummaryLabel>{t('사용 포인트')}</CartSummaryLabel>
            </CartSummaryCell>
          )}
          {isPointUsageEnabled && cartEarnPoint > 0 && (
            <CartSummaryCell isLabel>
              <CartSummaryLabel>{t('적립 포인트')}</CartSummaryLabel>
            </CartSummaryCell>
          )}
          <CartSummaryCell isLast isLabel>
            <CartSummaryLabelTotal>{t('총 결제금액')}</CartSummaryLabelTotal>
          </CartSummaryCell>
        </CartSummaryLabelColumn>

        <CartSummaryValueColumn>
          <CartSummaryCell isFirst>
            <CartSummaryValueLarge>{cartSummary.totalCount}개</CartSummaryValueLarge>
          </CartSummaryCell>
          <CartSummaryCell>
            <CartSummaryValue>{formatInteger(cartSummary.totalPrice)}원</CartSummaryValue>
          </CartSummaryCell>
          <CartSummaryCell>
            <CartSummaryValue>{formatInteger(cartSummary.totalDiscount)}원</CartSummaryValue>
          </CartSummaryCell>
          <CartSummaryCell>
            <CartSummaryValueOutstanding>{formatInteger(cartSummary.totalOutstanding)}원</CartSummaryValueOutstanding>
          </CartSummaryCell>
          {cartSummary.totalUsedPoint > 0 && (
            <CartSummaryCell>
              <CartSummaryValue>{formatInteger(cartSummary.totalUsedPoint)} P</CartSummaryValue>
            </CartSummaryCell>
          )}
          {isPointUsageEnabled && cartEarnPoint > 0 && (
            <CartSummaryCell>
              <CartSummaryValue>{formatInteger(cartEarnPoint)} P</CartSummaryValue>
            </CartSummaryCell>
          )}
          <CartSummaryCell isLast>
            <CartSummaryValueTotal>{formatInteger(netTotalFinalPrice)}원</CartSummaryValueTotal>
          </CartSummaryCell>
        </CartSummaryValueColumn>
      </CartSummaryTable>

    </CartSection>
  );
};

export default EnterSaleInfoModalCartSection;
